import { format } from 'd3-format';
import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { getDayNames, getMonthNames } from 'src/util/i18n/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import { formatDateTimeFull } from 'src/util/i18n/helpers';

/**
 * Description
 * @param {object} props
 * @param {object} props.volumeCharge
 * @returns {React.ReactElement} - AdminVolumeChargeShow component
 */
function AdminVolumeChargeShow({ volumeCharge = null }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  if (!volumeCharge) {
    return <Loading />;
  }

  const timeOfDayTypeToString = (timeOfDay) => {
    const hours = format('02d')(timeOfDay.hours);
    const minutes = format('02d')(timeOfDay.minutes);
    const seconds = timeOfDay.seconds > 0 ? format('02d')(timeOfDay.seconds) : null;
    const nanos = timeOfDay.nanos > 0 ? format('09d')(timeOfDay.nanos) : null;

    let timeOfDayString = [hours, minutes].join(':');
    if (seconds === null) {
      return timeOfDayString;
    }
    timeOfDayString = [timeOfDayString, seconds].join(':');
    if (nanos === null) {
      return timeOfDayString;
    }
    return [timeOfDayString, nanos].join('.');
  };

  const {
    uuid, identifier, externalIdentifier, title, rate, description, volumeUnit, importFlag,
    exportFlag, ignorePublicHolidays, timezone, monthsOfYear, daysOfWeek, timesOfDay, createdAt,
    updatedAt, tariff,
  } = volumeCharge;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.page.title', defaultMessage: 'Enosi Admin - Volume Charge - {title}' },
    { title },
  );
  const breadcrumbsAdmin = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbsVolumeCharges = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.breadcrumbs.properties.label', defaultMessage: 'Volume Charges' },
  );

  // Card headers
  const volumeChargeHeader = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.card.volume_charge_details.header', defaultMessage: 'Volume Charge details' },
  );
  const monthsOfYearHeader = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.card.months_of_year.header', defaultMessage: 'Months of Year' },
  );
  const daysOfWeekHeader = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.card.days_of_week.header', defaultMessage: 'Days of Week' },
  );
  const timesOfDayHeader = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.card.times_of_day.header', defaultMessage: 'Times of Day' },
  );

  // Table column headers
  const tableDescriptionLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.description.label', defaultMessage: 'Description' },
  );
  const tableRateLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.rate.label', defaultMessage: 'Rate' },
  );
  const tableTitleLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.title.label', defaultMessage: 'Title' },
  );
  const tableVolumeUnitLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.volume_unit.label', defaultMessage: 'Volume unit' },
  );
  const tableIdLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.id.label', defaultMessage: 'ID' },
  );
  const tableExternalIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.external_identifier.label', defaultMessage: 'Ext ID' },
  );
  const tableImportFlagLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.import_flag.label', defaultMessage: 'Import flag' },
  );
  const tableExportFlagLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.export_flag.label', defaultMessage: 'Export flag' },
  );
  const tableIgnorePublicHolidaysLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.ignore_public_holidays.label', defaultMessage: 'Ignore public holidays' },
  );
  const tableTimezoneLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.timezone.label', defaultMessage: 'Timezone' },
  );
  const tableTariffLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.tariff.label', defaultMessage: 'Tariff' },
  );
  const tableMonthActiveLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.month_active.label', defaultMessage: 'Month active' },
  );
  const tableCreatedAtLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.created_at.label', defaultMessage: 'Created at' },
  );
  const tableUpdatedAtLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.updated_at.label', defaultMessage: 'Updated at' },
  );
  const tableIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.identifier.label', defaultMessage: 'Identifier' },
  );
  const tableDaysActiveLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.days_active.label', defaultMessage: 'Days active' },
  );
  const tableTimeOfDaysActiveLabel = intl.formatMessage(
    { id: 'admin.admin_volume_charge.admin_volume_charge_show.table.times_of_day_active.label', defaultMessage: 'Times of day active' },
  );
  const monthNames = getMonthNames(intl.formatMessage);
  const dayNames = getDayNames(intl.formatMessage);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsAdmin, path: '/admin' },
          { name: breadcrumbsVolumeCharges, path: '/admin/volume-charges' },
          { name: title },
        ]}
      />

      <h1>
        {title}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card className="mb-4">
            <CardHeader>
              {volumeChargeHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{tableIdLabel}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title="External identifier">{tableExternalIdentifierLabel}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableTitleLabel}</th>
                  <td>{title}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableIdentifierLabel}</th>
                  <td>{identifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableDescriptionLabel}</th>
                  <td>{description}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableRateLabel}</th>
                  <td>
                    {valuePerUnitWithStrong(intl, intlCurrency, rate)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableVolumeUnitLabel}</th>
                  <td>{volumeUnit}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableImportFlagLabel}</th>
                  <td>{importFlag}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableExportFlagLabel}</th>
                  <td>{exportFlag}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableIgnorePublicHolidaysLabel}</th>
                  <td>{ignorePublicHolidays}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableTimezoneLabel}</th>
                  <td>{timezone}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableTariffLabel}</th>
                  <td>
                    {tariff && (
                      <>
                        <Link to={`/admin/tariffs/${tariff.uuid}`}>
                          <strong>{tariff.title}</strong>
                        </Link>
                        <br />
                        <UUID uuid={tariff.uuid} />
                        <br />
                        <BadgeActive start={tariff.active.start} finish={tariff.active.finish} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableCreatedAtLabel}</th>
                  <td>
                    {createdAt
                      && formatDateTimeFull(intl, createdAt)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableUpdatedAtLabel}</th>
                  <td>
                    {updatedAt
                      && formatDateTimeFull(intl, updatedAt)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card className="mb-4">
            <CardHeader>
              {monthsOfYearHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col" colSpan={monthsOfYear.length}>{tableMonthActiveLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {monthsOfYear.map((el) => (
                    <td key={`month-of-year-${el}`}>
                      {monthNames[el]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {daysOfWeekHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col" colSpan={daysOfWeek.length}>{tableDaysActiveLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {daysOfWeek.map((el) => (
                    <td key={`day-of-week-${el}`}>
                      {dayNames[el]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {timesOfDayHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col" colSpan={timesOfDay.length}>{tableTimeOfDaysActiveLabel}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {timesOfDay.map(({ start, finish }) => (
                    <td key={`time-of-day-${timeOfDayTypeToString(start)}-${timeOfDayTypeToString(finish)}`}>
                      {[timeOfDayTypeToString(start), timeOfDayTypeToString(finish)].join(' to ')}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminVolumeChargeShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  volumeCharge: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default createFragmentContainer(
  AdminVolumeChargeShow,
  {
    volumeCharge: graphql`
      fragment AdminVolumeChargeShow_volumeCharge on VolumeCharge {
        uuid
        identifier
        externalIdentifier
        title
        rate
        description
        volumeUnit
        importFlag
        exportFlag
        ignorePublicHolidays
        timezone
        monthsOfYear
        daysOfWeek
        timesOfDay {
          start  { hours minutes seconds nanos }
          finish { hours minutes seconds nanos }
        }
        createdAt
        updatedAt
        tariff {
          uuid
          title
          active { start finish }
        }
      }
    `,
  },
);
